import { useState, useEffect } from 'react'
import { ConnectorManager } from '../../ConnectorManager'

const STORAGE_KEY = 'zerodha-favorites'

export function useNintradeFavorites() {
  const [favorites, setFavorites] = useState<string[]>(() => {
    // Initialize from localStorage if available
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        try {
          const parsedFavorites = JSON.parse(stored)
          console.log('Loaded favorites from localStorage:', parsedFavorites)
          return parsedFavorites
        } catch (e) {
          console.error('Error parsing stored favorites:', e)
        }
      }
    }
    // Default favorites if nothing in localStorage
    const defaultFavorites = [
      'RELIANCE',
      'TCS',
      'HDFCBANK',
      'INFY',
      'ICICIBANK',
      'HINDUNILVR',
      'SBIN',
      'BAJFINANCE',
      'BHARTIARTL',
      'KOTAKBANK'
    ]
    console.log('Using default favorites')
    return defaultFavorites
  })

  // Fetch favorites from the connector only on mount
  useEffect(() => {
    const fetchFavoritesFromConnector = async () => {
      try {
        // Check if we already have favorites in localStorage
        const storedFavorites = localStorage.getItem(STORAGE_KEY)
        if (storedFavorites) {
          // If we have stored favorites, use them and don't override with connector data
          return
        }
        
        // Only fetch from connector if we don't have localStorage data
        const connectorManager = ConnectorManager.getInstance()
        const zerodhaConnector = connectorManager.getConnector('zerodha')
        
        if (
          zerodhaConnector &&
          zerodhaConnector.isConnectedToBroker &&
          zerodhaConnector.isConnectedToBroker()
        ) {
          const connectorFavorites = await zerodhaConnector.getFavorites()
          if (connectorFavorites && connectorFavorites.length > 0) {
            setFavorites(connectorFavorites)
          }
        }
      } catch (error) {
        console.error('Error fetching favorites from Zerodha connector:', error)
      }
    }

    fetchFavoritesFromConnector()
  }, []) // <-- empty dependency array

  // Save to localStorage whenever favorites change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites))
        console.log('Saved favorites to localStorage:', favorites)
      } catch (error) {
        console.error('Error saving favorites to localStorage:', error)
      }
    }
  }, [favorites])

  const addFavorite = (symbol: string) => {
    const formattedSymbol = symbol.toUpperCase()
    if (!favorites.includes(formattedSymbol)) {
      setFavorites([...favorites, formattedSymbol])
      return true
    }
    return false
  }

  const removeFavorite = (symbol: string) => {
    const formattedSymbol = symbol.toUpperCase()
    if (favorites.includes(formattedSymbol)) {
      setFavorites(favorites.filter(fav => fav !== formattedSymbol))
      return true
    }
    return false
  }

  const toggleFavorite = (symbol: string) => {
    const formattedSymbol = symbol.toUpperCase()
    if (favorites.includes(formattedSymbol)) {
      removeFavorite(formattedSymbol)
      return false
    } else {
      addFavorite(formattedSymbol)
      return true
    }
  }

  const isFavorite = (symbol: string) => {
    return favorites.includes(symbol.toUpperCase())
  }

  return {
    favorites,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite
  }
}
